import { uniq } from 'lodash';
import { useQuery } from 'react-query';
import { ContentTypeTag } from '../../models';
import { ReadingList } from '../../models/ReadingList';
import { DEFAULT_READING_LIST } from '../../pages/MyContent/ArticlesTab/ArticlesTab.constants';
import { searchTags } from '../../services/tags/tags.service';
import { isCompanyTag } from '../../utils/tagUtils';
import { queryKeys } from '../queryKeyFactory';
import useDefaultReadingList from './useDefaultReadingList';
import { mapReadingListArticleDtoToReadingListArticle } from './utils/readingList.mappers';

interface UseDefaultReadingListWithArticleTagsOptions {
  keepPreviousData?: boolean;
  readingListName?: string;
  enabled?: boolean;
}

interface UseDefaultReadingListWithArticleTagsResult {
  data: ReadingList | undefined;
  isError: boolean;
  isLoading: boolean;
  isFetching: boolean;
}

export default ({
  keepPreviousData = true,
  readingListName = DEFAULT_READING_LIST,
  enabled = true,
}: UseDefaultReadingListWithArticleTagsOptions = {}): UseDefaultReadingListWithArticleTagsResult => {
  const { data, isLoading, isFetching, isError } = useDefaultReadingList({
    keepPreviousData,
    readingListName,
    enabled,
  });

  const query = useQuery<ReadingList | undefined, Error>(
    queryKeys.fetchReadingLists.defaultReadingListWithTags(data),
    async () => {
      const readingList = data!;
      const articlesTags = uniq(
        data?.articles
          .map((article) => article.tags)
          .flat()
          .filter((tag) => isCompanyTag(tag) || Object.values<string>(ContentTypeTag).includes(tag))
      );
      const tags = articlesTags?.length ? await searchTags(articlesTags) : {};

      return {
        id: readingList.id,
        articles: mapReadingListArticleDtoToReadingListArticle(readingList.articles, tags),
        createdAt: readingList.createdAt,
        name: readingList.name,
        updatedAt: readingList.updatedAt,
      };
    },
    { enabled: enabled && !isLoading && data !== undefined, keepPreviousData }
  );

  return {
    data: query.data,
    isLoading: query.isLoading || isLoading,
    isFetching: query.isFetching || isFetching,
    isError: query.isError || isError,
  };
};
